import client from "./client";

const path = (code) => encodeURIComponent(code);

/** Looks a session up by its join code. Rejects with a 404 response when it does not exist. */
export const getQueue = (code) => client.get(`/Queue/GetQueue/${path(code)}`).then((r) => r.data);

/** The songs queued in a session, in queue order (each carries its Sort and the host's Transpose). */
export const getQueueSongs = (code) => client.get(`/Queue/GetSongs/${path(code)}`).then((r) => r.data);

/** Creates a new session and returns it (including its generated Code). */
export const createQueue = () => client.post("/Queue/Create").then((r) => r.data);

/** Appends a song to the queue and returns it (with Sort). Idempotent, so safe to retry. */
export const addSong = (code, songId) =>
  client.put(`/Queue/AddSong/${path(code)}/${songId}`, null, { retry: true }).then((r) => r.data);

/** Queues a song right after another one (0 puts it first) and returns it. Idempotent. Host only. */
export const insertSong = (code, songId, afterSongId) =>
  client.put(`/Queue/InsertSong/${path(code)}/${songId}/${afterSongId || 0}`, null, { retry: true }).then((r) => r.data);

export const removeSong = (code, songId) =>
  client.delete(`/Queue/RemoveSong/${path(code)}/${songId}`, { retry: true });

// Not retried: a replayed stale "current position" update would move everyone backwards.
export const setSong = (code, songId) =>
  client.put(`/Queue/SetSong/${path(code)}/${songId}`).then((r) => r.data);

export const setVerse = (code, verseNum) =>
  client.put(`/Queue/SetVerse/${path(code)}/${verseNum}`).then((r) => r.data);

/** Removes every queued song and deselects the current song (host only). */
export const clearSongs = (code) => client.delete(`/Queue/ClearSongs/${path(code)}`);

/** Ends the session for everyone (host only); resolves with the session's summary. */
export const endSession = (code) => client.delete(`/Queue/End/${path(code)}`).then((r) => r.data);

/** Puts the listed songs first, in that order (host only); resolves with the new order of all queued ids. */
export const reorder = (code, songIds) =>
  client.put(`/Queue/Reorder/${path(code)}`, { SongIds: songIds }).then((r) => r.data);

/** Marks a queued song as sung or not yet sung (host only). Idempotent, so safe to retry. */
export const setSung = (code, songId, sung) =>
  client.put(`/Queue/SetSung/${path(code)}/${songId}/${sung}`, null, { retry: true });

/** The host's key for a queued song, in semitones from the written key (host only). Idempotent. */
export const setTranspose = (code, songId, semitones) =>
  client.put(`/Queue/SetTranspose/${path(code)}/${songId}/${semitones}`, null, { retry: true });

/** A heart from anyone in the room; resolves with the session's count. */
export const react = (code) => client.post(`/Queue/React/${path(code)}`).then((r) => r.data);

/** Asks the host for a song; resolves with the request (the existing one when it was already asked for). */
export const requestSong = (code, songId, name) =>
  client.post(`/Queue/RequestSong/${path(code)}/${songId}`, { Name: name || null }).then((r) => r.data);

/** The pending requests of a session, oldest first. Host only. */
export const getRequests = (code) => client.get(`/Queue/Requests/${path(code)}`).then((r) => r.data);

/** Queues a requested song right after the current one (or at the end) and drops the request. Host only. */
export const acceptRequest = (code, requestId, atEnd) =>
  client.put(`/Queue/AcceptRequest/${path(code)}/${requestId}/${Boolean(atEnd)}`).then((r) => r.data);

/** Drops a request without queueing it. Host only. */
export const dismissRequest = (code, requestId) =>
  client.delete(`/Queue/DismissRequest/${path(code)}/${requestId}`, { retry: true });
